import { useRef, useState } from 'react';
import { Minus, Plus } from '@phosphor-icons/react';
import type { Expertise } from '../../content/site';
import { gsap, useGSAP, EASE, MOTION_OK } from '../../lib/gsap';
import { prefersReducedMotion } from '../../lib/useReducedMotion';
import { EXPERTISE_ICONS, surfaceAt } from './surfaces';
import { ExpertiseFacts, ExpertiseSummary } from './ExpertiseDetails';

const ids = (item: Expertise) => ({
	button: `expertise-${item.id}-mobile`,
	panel: `expertise-${item.id}-mobile-detail`,
});

/**
 * Below 1024px: a vertical accordion, one item open at a time (the first by default,
 * tapping the open one closes it). Headers are 64px buttons in <h3>s with aria-expanded
 * and aria-controls. Panels open with a GSAP height tween inside overflow-hidden
 * (instant under reduced motion); closed panels are visibility:hidden so their content
 * leaves the accessibility tree and the tab order.
 */
export function VerticalAccordion({ items }: { items: Expertise[] }) {
	const listRef = useRef<HTMLUListElement>(null);
	const [open, setOpen] = useState<number | null>(0);
	const openRef = useRef<number | null>(0);

	const { contextSafe } = useGSAP(
		() => {
			gsap.utils.toArray<HTMLElement>('[data-acc-panel]').forEach((panel, index) => {
				if (index !== openRef.current) gsap.set(panel, { height: 0, visibility: 'hidden' });
			});

			const mm = gsap.matchMedia();
			mm.add(MOTION_OK, () => {
				gsap.from('[data-acc-item]', {
					y: 30,
					opacity: 0,
					duration: 1,
					ease: EASE,
					stagger: 0.08,
					scrollTrigger: { trigger: listRef.current, start: 'top 85%', once: true },
				});
			});
		},
		{ scope: listRef },
	);

	const toggle = contextSafe((index: number, header: HTMLElement) => {
		const list = listRef.current;
		if (!list) return;
		const panels = gsap.utils.toArray<HTMLElement>('[data-acc-panel]', list);
		const previous = openRef.current;
		const next = previous === index ? null : index;
		const instant = prefersReducedMotion();

		// When an open item above the tapped one collapses, the tapped header would slide
		// up the screen (out of view on a phone). Keep it where the finger left it.
		const anchorTop = header.getBoundingClientRect().top;
		const keepHeader = () => {
			const drift = header.getBoundingClientRect().top - anchorTop;
			if (Math.abs(drift) > 0.5) window.scrollBy(0, drift);
		};

		if (previous !== null) {
			const panel = panels[previous];
			if (instant) {
				gsap.set(panel, { height: 0, visibility: 'hidden' });
				keepHeader();
			} else
				gsap.to(panel, {
					height: 0,
					duration: 0.55,
					ease: EASE,
					overwrite: true,
					onUpdate: keepHeader,
					onComplete: () => gsap.set(panel, { visibility: 'hidden' }),
				});
		}

		if (next !== null) {
			const panel = panels[next];
			gsap.set(panel, { visibility: 'visible' });
			if (instant) gsap.set(panel, { height: 'auto' });
			else {
				gsap.to(panel, { height: 'auto', duration: 0.75, ease: EASE, overwrite: true });
				gsap.fromTo(
					panel.firstElementChild,
					{ opacity: 0, y: 14 },
					{ opacity: 1, y: 0, duration: 0.7, delay: 0.08, ease: EASE, overwrite: true },
				);
			}
		}

		openRef.current = next;
		setOpen(next);
	});

	return (
		<ul ref={listRef} className="grid gap-3">
			{items.map((item, index) => {
				const isOpen = open === index;
				const surface = surfaceAt(index);
				const ItemIcon = EXPERTISE_ICONS[item.icon];
				const ToggleIcon = isOpen ? Minus : Plus;
				const { button, panel } = ids(item);

				return (
					<li key={item.id} data-acc-item className={`overflow-hidden rounded-card ${surface.bg}`}>
						<h3>
							<button
								type="button"
								id={button}
								aria-expanded={isOpen}
								aria-controls={panel}
								onClick={(event) => toggle(index, event.currentTarget)}
								className={`grid min-h-16 w-full grid-cols-[1.75rem_minmax(0,1fr)_2.75rem] items-center gap-4 rounded-card px-5 py-3 text-left focus-visible:outline-offset-[-5px] md:px-7 ${surface.ink} ${surface.focus}`}
							>
								<ItemIcon aria-hidden size={28} weight="light" className={surface.mark} />
								<span className="font-display text-[1.375rem] font-bold leading-tight tracking-[-0.02em]">
									{item.title}
								</span>
								<span
									aria-hidden
									className={`grid h-11 w-11 place-items-center rounded-full border ${surface.edge}`}
								>
									<ToggleIcon size={18} weight="bold" />
								</span>
							</button>
						</h3>
						<div
							id={panel}
							role="region"
							aria-labelledby={button}
							data-acc-panel
							className="overflow-hidden"
						>
							<div className="px-5 pb-7 pt-1 md:px-7 md:pb-9">
								<ExpertiseSummary item={item} surface={surface} />
								<ExpertiseFacts item={item} surface={surface} className="mt-6" />
							</div>
						</div>
					</li>
				);
			})}
		</ul>
	);
}
